import { useEffect, useRef } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useHandleSignInCallback } from '@logto/react'
import { Loader2 } from 'lucide-react'

import { AuthLoadingTimeout } from '@/auth/AuthLoadingTimeout'
import { useAuthLoadingWatchdog } from '@/auth/useAuthLoadingWatchdog'
import { captureSafeMessage } from '@/lib/sentry-instrument'

export const Route = createFileRoute('/callback')({
  component: CallbackPage,
})

/** If ``useHandleSignInCallback`` is still loading after this many ms, treat
 *  the callback as hung and emit a Sentry signal. Logto's code-exchange round
 *  trip is normally well under 2s; 15s is generous enough that a slow Entra
 *  redirect on cold cache doesn't fire a false positive, while still surfacing
 *  the real "stuck on /callback with a spinner" prod bug Max reported. */
const CALLBACK_HANG_THRESHOLD_MS = 15_000

export function CallbackPage() {
  const navigate = useNavigate()
  // Initialized lazily inside the effect — `Date.now()` is impure and the
  // react-hooks/purity rule forbids calling it during render.
  const startedAtRef = useRef<number | null>(null)
  const hangReportedRef = useRef(false)
  const { isLoading, error } = useHandleSignInCallback(() => {
    navigate({ to: '/dashboard', replace: true })
  })

  // The same SDK-level hang that bites the authenticated layout can bite the
  // PKCE code-exchange too: a stalled /oidc/token POST leaves isLoading=true
  // forever and the user stares at a spinner mid-handshake. Bound it here as
  // well so the recovery card fires instead.
  const stuck = useAuthLoadingWatchdog(isLoading)

  // Surface a hang detector so the prod "/callback never resolves" bug Max
  // saw lands in Sentry instead of being invisible to on-call. We do NOT
  // include any auth code / state / token — only the route name, the
  // bounded elapsed time, and the loading flag.
  useEffect(() => {
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now()
    }
    const startedAt = startedAtRef.current
    if (!isLoading || hangReportedRef.current) return
    const elapsedAtMount = Date.now() - startedAt
    const remaining = Math.max(CALLBACK_HANG_THRESHOLD_MS - elapsedAtMount, 0)
    const timer = window.setTimeout(() => {
      hangReportedRef.current = true
      captureSafeMessage('auth.callback_hang', 'warning', {
        route: '/callback',
        operation: 'logto_handle_callback',
        elapsed_ms: Date.now() - startedAt,
      })
    }, remaining)
    return () => window.clearTimeout(timer)
  }, [isLoading])

  // Logto's callback hook surfaces protocol-level failures (state mismatch,
  // expired code, JWKS fetch error) through its ``error`` field. The user
  // sees a blank page in that case — capture the error so we can diagnose.
  useEffect(() => {
    if (!error) return
    captureSafeMessage('auth.callback_failed', 'error', {
      route: '/callback',
      operation: 'logto_handle_callback',
      exc_type: error instanceof Error ? error.name : typeof error,
    })
  }, [error])

  if (stuck) {
    return (
      <AuthLoadingTimeout
        title="Finishing sign-in is taking too long"
        description="We couldn't complete the sign-in handshake. Try signing in again."
        reason="callback_timeout"
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }
  return null
}
